import { SyntheticEvent, useEffect, useState } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { Box } from "@mui/system";
import { Theme, Button, useTheme, Dialog, useMediaQuery } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { FieldProps } from "@/core/components/Field";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { FlightSummaryTable } from "./FlightSummaryTable";
import { FlightSummaryEdit } from "./FlightSummaryEdit/FlightSummaryEdit";
import { camundaApi } from "@/core/api/camundaApi";
import {
  AirlineData,
  AirportData,
  ButtonVariant,
  MaterialColors,
  TextAlign,
} from "@/core/types/types";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import { Loading } from "@/core/components/Loading/Loading";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";

const PREVIOUS_ANSWERS_VARIABLES: string[] = [
  "leadReferenceNumber",
  "flightProblem",
];

interface FormData {
  [key: string]: string | AirlineData | AirportData | boolean | undefined;
}

const styles = (theme: Theme) => ({
  modalHeader: {
    position: "fixed",
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 2,
    top: 0,
    padding: theme.spacing(2),
  },
  modalContent: {
    [theme.breakpoints.up("md")]: {
      width: "100%",
      maxWidth: "960px",
      margin: "0 auto",
      padding: `0 ${theme.spacing(8)}`,
      overflow: "auto",
    },
    [theme.breakpoints.down("md")]: {
      position: "inherit",
      width: "100%",
      overflow: "hidden",
    },
  },
  modalFooter: {
    position: "fixed",
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 1,
    bottom: 0,
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey.A700}`,
    backgroundColor: theme.palette.background.default,
  },
});

const FlightSummary = ({ field }: FieldProps) => {
  const [didEdit, setDidEdit] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const classes: any = useCustomStyles(styles);
  const theme = useTheme();
  const formMethods = useForm();
  const { trigger, control } = formMethods;
  const formValues: FormData = useWatch({ control });
  const { initialise } = useClaimCheck();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md"));

  const handleSave = async (e: SyntheticEvent) => {
    trigger().then(async (isValid: boolean) => {
      if (isValid) {
        const formNotEmpty = Object.keys(formValues).length > 0;
        formNotEmpty &&
          (await camundaApi.updateProcessVariables(formValues).then(() => {
            e.preventDefault();
            setDidEdit(true);
            initialise();
          }));
        setIsEditing(false);
      }
    });
  };

  const handleClose = () => {
    trigger().then(async (isValid) => {
      if (isValid) {
        setIsEditing(false);
        setDidEdit(false);
      }
    });
  };

  const {
    properties: { requiredInputVariables },
  } = field;

  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }

  const { result: previousAnswers } = useGetPreviousAnswers(
    requiredInputVariables
  );

  // Some variables that are not in the form, we will need take from previous answers
  // for example flight problem and reference number
  const getVariableToRender = (variableName: string) => {
    if (didEdit) {
      const isPreviousAnswer: Boolean =
        PREVIOUS_ANSWERS_VARIABLES.includes(variableName);
      return isPreviousAnswer
        ? previousAnswers[variableName]
        : formValues[variableName];
    } else {
      return previousAnswers[variableName];
    }
  };

  const leadReferenceNumber: string = getVariableToRender(
    "leadReferenceNumber"
  );
  const departureAirport: AirportData = getVariableToRender("originIATA");
  const arrivalAirport: AirportData = getVariableToRender("destinationIATA");
  const airline: AirlineData = getVariableToRender("airline");
  const departureDate: string = getVariableToRender("departureDate");
  const flightProblem: string = getVariableToRender(
    "originallySelectedFlightProblem"
  );
  const customerGender: string = getVariableToRender("customerGender");
  const customerFirstName: string = getVariableToRender("customerFirstName");
  const customerLastName: string = getVariableToRender("customerLastName");
  const customerEmail: string = getVariableToRender("customerEmail");
  const customerIsMinor: boolean = getVariableToRender("isMinor");
  const numberOfPassengers: number = getVariableToRender("numberOfPassengers");
  const isDataReadyToRender: boolean = Boolean(
    leadReferenceNumber &&
      departureAirport &&
      arrivalAirport &&
      departureDate &&
      airline &&
      flightProblem &&
      customerFirstName &&
      customerLastName &&
      numberOfPassengers
  );

  useEffect(() => {
    trigger();
  }, [formValues]);

  return (
    <>
      <Dialog
        fullScreen={true}
        open={isEditing}
        aria-labelledby="edit-passenger-information"
        aria-describedby="Edit the passenger contact information."
        disablePortal
      >
        <Box sx={classes.modalHeader}>
          <HighlightOffIcon
            sx={{ cursor: "pointer", fontSize: theme.spacing(6) }}
            onClick={handleClose}
          />
        </Box>

        <Box sx={classes.modalContent}>
          <FormProvider {...formMethods}>
            <form>
              <FlightSummaryEdit
                departureAirport={departureAirport}
                arrivalAirport={arrivalAirport}
                departureDate={departureDate}
                airline={airline}
                customerGender={customerGender}
                customerFirstName={customerFirstName}
                customerLastName={customerLastName}
                customerEmail={customerEmail}
                customerIsMinor={customerIsMinor}
                numberOfPassengers={numberOfPassengers}
              />
            </form>
          </FormProvider>
        </Box>

        <Box sx={classes.modalFooter}>
          <Button
            color={MaterialColors.Primary}
            variant={ButtonVariant.CtaContained}
            sx={{
              width: isMobile ? "100%" : "25%",
              padding: theme.spacing(1.5, isMobile ? 2 : 8),
            }}
            onClick={(e) => handleSave(e)}
          >
            {t("save.label")}
          </Button>
        </Box>
      </Dialog>

      <Box
        display="flex"
        px={2}
        py={3}
        bgcolor={theme.palette.background.default}
      >
        {isDataReadyToRender ? (
          <>
            <FlightSummaryTable
              leadReferenceNumber={leadReferenceNumber}
              departureAirport={departureAirport?.name}
              arrivalAirport={arrivalAirport?.name}
              departureDate={departureDate}
              airlineName={airline?.name}
              flightProblem={flightProblem}
              customerFirstName={customerFirstName}
              customerLastName={customerLastName}
              numberOfPassengers={numberOfPassengers}
            />
            <EditIcon
              color={MaterialColors.Primary}
              sx={{ cursor: "pointer" }}
              onClick={() => setIsEditing(true)}
            />
          </>
        ) : (
          <Box mx="auto" textAlign={TextAlign.Center}>
            <Loading />
          </Box>
        )}
      </Box>
    </>
  );
};

export { FlightSummary };
